import React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import TuneIcon from '@mui/icons-material/Tune'
import PatternListView from './components/PatternListView'
import { MyIndicatorsList } from './indicatorBuilder'
import { usePatternContext, defaultPattern } from './contexts/PatternContext'
import useSaveCustomIndicator from '../../hooks/useSaveCustomIndicator'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { useSavePattern } from '../../hooks/useSavePattern'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'

const BuilderWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 16px)',
  overflow: 'hidden',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}))

const ContentContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(0),
  backgroundColor: theme.palette.background.custom1,
}))

function BuildArea(props: any) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {
    searchTerm,
    setSearchTerm,
    setEditingPattern,
    canUseCustomIndicators,
    builderTab,
    setBuilderTab,
    setEditingIndicator,
  } = usePatternContext()

  const showingIndicators = canUseCustomIndicators && builderTab === 'indicators'
  const { tradingHouse, usermeta, canViewPrivatePatterns } = useApplicationSuiteContext()

  // Setup save pattern functionality
  const { forceSave } = useSavePattern(
    // We're not modifying state directly here, this is just for the hook
    (pattern) => {},
    usermeta,
    tradingHouse,
    canViewPrivatePatterns
  )

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const { createIndicator } = useSaveCustomIndicator({
    gcid: usermeta?.gcid,
    tradingHouse,
  })

  // Create-on-entry (pattern parity): v1 exists before the editor opens
  const handleCreateIndicator = async () => {
    try {
      const saved = await createIndicator()
      setEditingIndicator({ editId: saved.id, doc: saved })
    } catch (error) {
      console.error('Error creating new indicator:', error)
    }
  }

  const createPattern = async () => {
    try {
      // Create a new pattern based on the default pattern template
      const newPattern = {
        ...defaultPattern,
        tradingHouse: tradingHouse,
      }

      // Save the pattern which will generate an ID and handle versioning
      const savedPattern = await forceSave(newPattern)
      if (savedPattern) {
        setEditingPattern(savedPattern)
      }
    } catch (error) {
      console.error('Error creating new pattern:', error)
    }
  }

  return (
    <BuilderWrapper>
      {/* Header */}
      <AppBar
        position="static"
        sx={{
          boxShadow: 'none',
          background: 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          mb: 1,
        }}
      >
        <Toolbar style={{ padding: 0, display: 'flex', gap: 8, minHeight: 56 }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={1}
            alignItems={isMobile ? '' : 'center'}
            sx={{ flexGrow: 1 }}
            mb={isMobile ? '8px' : 0}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {showingIndicators ? (
                <TuneIcon sx={{ color: theme.palette.primary.main }} />
              ) : (
                <AccountTreeIcon sx={{ color: theme.palette.primary.main }} />
              )}
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}
              >
                {showingIndicators ? 'Indicators' : 'Patterns'}
              </Typography>
            </Box>
            <Box display={'flex'} width={'100%'} gap={1} alignItems="center">
              <Box flexBasis={300} flexShrink={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder={showingIndicators ? 'Search indicators...' : 'Search patterns...'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ opacity: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.custom3',
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                    },
                  }}
                />
              </Box>
              {/* Placed after the search, mirroring the alerts page's
                  Your Alerts / Templates toggle */}
              {canUseCustomIndicators && (
                <ToggleButtonGroup
                  color="primary"
                  exclusive
                  size="small"
                  value={builderTab}
                  onChange={(_, value) => value && setBuilderTab(value)}
                  data-tour-id="builder-view-toggle"
                >
                  <ToggleButton value="patterns">Patterns</ToggleButton>
                  <ToggleButton value="indicators">Indicators</ToggleButton>
                </ToggleButtonGroup>
              )}
              <Box ml={'auto'}>
                {showingIndicators ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleCreateIndicator}
                    sx={{ flexShrink: 0 }}
                    data-tour-id="create-indicator-button"
                  >
                    Create Indicator
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={createPattern}
                    sx={{ flexShrink: 0 }}
                    data-tour-id="create-pattern-button"
                  >
                    Create Pattern
                  </Button>
                )}
              </Box>
            </Box>
          </Stack>
        </Toolbar>
      </AppBar>

      <ContentContainer>
        {showingIndicators ? (
          <Box sx={{ px: 1, py: 1.5 }}>
            <MyIndicatorsList />
          </Box>
        ) : (
          <PatternListView />
        )}
      </ContentContainer>
    </BuilderWrapper>
  )
}

export default BuildArea
